import { faGear } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'
import { useRef, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { InputCol } from '~/components/Form'
import { Label } from '~/components/Form/Input/Label'
import { TextInput } from '~/components/Form/Input/TextInput'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { Dropdown } from '~/components/Global/Interactions/Dropdown/Dropdown'
import { Toggle } from '~/components/Global/Interactions/Toggle/Toggle'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'
import type { ConfigName } from '~/domains/configuration/types'
import {
  actionsListOptionLine,
  actionsListOptionLineLabel
} from './NextActionsBlock.css'

export const NextActionsConfigDropdown = (): ReactElement => {
  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const configDropdownRef = useRef<HTMLDivElement>(null)
  const { data: todayBefore } = useConfig('dashboard.next_actions_today_before')
  const { data: todayAfter } = useConfig('dashboard.next_actions_today_after')
  const configMutation = useUpdateConfig()

  const enabledTodayBefore = todayBefore !== null
  const enabledTodayAfter = todayAfter !== null

  const handleButtonClick = () => {
    setIsConfigOpen(!isConfigOpen)
  }

  const handleConfigSave = useDebouncedCallback(
    (configKey: ConfigName, value: number | null) => {
      configMutation.mutate({
        name: configKey,
        value
      })
    },
    300
  )

  return (
    <div ref={configDropdownRef} style={{ position: 'relative' }}>
      <Button
        variation={ButtonVariation.Neutral}
        onClick={handleButtonClick}
        narrow
        buttonSize="extraSmall"
      >
        <FontAwesomeIcon icon={faGear} />
      </Button>
      <Dropdown
        isOpen={isConfigOpen}
        dropdownRef={configDropdownRef}
        onclose={() => setIsConfigOpen(false)}
        placement="bottom-end"
      >
        <InputCol>
          <Label className={actionsListOptionLineLabel}>
            Afficher les actions passées
            <div className={actionsListOptionLine}>
              <Toggle
                defaultState={enabledTodayBefore}
                onChange={(checked) => {
                  if (!checked) {
                    handleConfigSave(
                      'dashboard.next_actions_today_before',
                      null
                    )
                  } else {
                    handleConfigSave('dashboard.next_actions_today_before', 0)
                  }
                }}
              />
              <TextInput
                type="number"
                standalone
                small
                min={0}
                max={99}
                defaultValue={todayBefore?.toString() ?? ''}
                onChange={(e) => {
                  const value = Number.parseInt(
                    (e.target as HTMLInputElement).value,
                    10
                  )
                  if (!Number.isNaN(value) && value >= 0 && value <= 99) {
                    handleConfigSave(
                      'dashboard.next_actions_today_before',
                      value
                    )
                  }
                }}
                disabled={!enabledTodayBefore}
                trailingText="jours"
              />
            </div>
          </Label>

          <Label className={actionsListOptionLineLabel}>
            Limiter les actions futures
            <div className={actionsListOptionLine}>
              <Toggle
                defaultState={enabledTodayAfter}
                onChange={(checked) => {
                  if (!checked) {
                    handleConfigSave('dashboard.next_actions_today_after', null)
                  } else {
                    handleConfigSave('dashboard.next_actions_today_after', 0)
                  }
                }}
              />
              <TextInput
                type="number"
                standalone
                small
                min={0}
                max={99}
                defaultValue={todayAfter?.toString() ?? ''}
                onChange={(e) => {
                  const value = Number.parseInt(
                    (e.target as HTMLInputElement).value,
                    10
                  )
                  if (!Number.isNaN(value) && value >= 0 && value <= 99) {
                    handleConfigSave(
                      'dashboard.next_actions_today_after',
                      value
                    )
                  }
                }}
                disabled={!enabledTodayAfter}
                trailingText="jours"
              />
            </div>
          </Label>
        </InputCol>
      </Dropdown>
    </div>
  )
}
